import {find, head, isEmpty, map} from 'lodash';
import {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {filesManagerApi} from 'api';
import {notifier, DataWrapper, DropZone, ConfirmModal, InvalidFileTypeModal} from 'components';
import {TRANSLATION_KEYS} from 'locale';
import {ApiError, FilesAreaContent} from 'types';
import {apiUtils} from 'utils';

import {FileInfo, FilesManagerProps} from '../types';

import {FilePreview} from './FilePreview';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.FILES_MANAGER;

type Props = FilesManagerProps & {
    selectedFilePath?: string;
    selectable?: boolean;
    imageSize?: 'md' | 'lg';
    imageOnly?: boolean;
    onSelect?: (filePath: string) => void;
    onRemoveSelected?: () => void;
};

export const FilesManager = ({
    area, imageSize, ownerType, ownerId, selectedFilePath: selectedFilePathProp, imageOnly,
    selectable = true, onSelect: onSelectProp, onRemoveSelected
}: Props) => {
    const {t} = useTranslation();
    const [content, setContent] = useState<FilesAreaContent>(null);
    const [contentLoading, setContentLoading] = useState<boolean>(false);
    const [actionLoading, setActionLoading] = useState<boolean>(false);
    const [confirmRemoveModalProps, setConfirmRemoveModalProps] = useState<{open: boolean, data?: FileInfo}>({open: false});
    const [invalidFileTypeModalProps, setInvalidFileTypeModalProps] = useState<{open: boolean, error?: ApiError}>({open: false});
    const [selectedFilePath, setSelectedFilePath] = useState<string>(selectedFilePathProp);

    const fetchContent = useCallback(async () => {
        try {
            setContentLoading(true);
            const contentResponse = await filesManagerApi.getFolderContentFull({type: ownerType, id: ownerId}, area);
            setContent(contentResponse);
            return contentResponse;
        } catch {
            notifier.error({details: t(T_KEYS.MESSAGES.FETCH_UNEXPECTED_ERROR)});
            setContent(null);
        } finally {
            setContentLoading(false);
        }
    }, [t, ownerType, ownerId, area]);

    const onSelect = useCallback(({path}: FileInfo) => {
        if (selectable) {
            const newPath = path === selectedFilePath ? '' : path;
            setSelectedFilePath(newPath);
            onSelectProp?.(newPath);
        }
    }, [onSelectProp, selectable, selectedFilePath]);

    const onRemove = useCallback(async ({path, name}: FileInfo) => {
        try {
            setActionLoading(true);
            await filesManagerApi.remove({type: ownerType, id: ownerId}, area, name);
            if (path === selectedFilePath) {
                onSelect({path: null, name: null});
                onRemoveSelected();
            }
            fetchContent();
        } catch {
            notifier.error({details: t(T_KEYS.MESSAGES.DELETE_UNEXPECTED_ERROR)});
        } finally {
            setActionLoading(false);
        }
    }, [t, ownerType, ownerId, area, selectedFilePath, onSelect, fetchContent, onRemoveSelected]);

    const onClickRemove = useCallback((data: FileInfo) => {
        setConfirmRemoveModalProps({open: true, data});
    }, []);

    const onCreate = useCallback(async (files: {file: File, base64: string}[]) => {
        try {
            if (isEmpty(files)) {
                notifier.error({details: t(T_KEYS.MESSAGES.INVALID_SELECTION)});
                return;
            }
            // Support only single file upload
            const file = head(files);
            if (file) {
                setActionLoading(true);
                await filesManagerApi.create({type: ownerType, id: ownerId}, area, file.file.name, file.base64);
                const currentContent = await fetchContent();
                const lastUploadedFile = find(currentContent?.files, {name: file.file.name});
                if (lastUploadedFile) {
                    onSelect(lastUploadedFile);
                }
            }
        } catch (e: any) {
            const error = e as ApiError;
            if (apiUtils.isFileTypeError(error)) {
                setInvalidFileTypeModalProps({open: true, error});
            } else {
                notifier.error({details: t(T_KEYS.MESSAGES.UPLOAD_UNEXPECTED_ERROR)});
            }
        } finally {
            setActionLoading(false);
        }
    }, [t, ownerType, ownerId, area, fetchContent, onSelect]);

    const onCloseConfirmRemoveModal = useCallback((accepted: boolean, data: FileInfo) => {
        setConfirmRemoveModalProps({open: false});
        if (accepted) {
            onRemove(data);
        }
    }, [onRemove]);

    const onCloseInvalidFileTypeModal = useCallback(() => {
        setInvalidFileTypeModalProps({open: false});
    }, []);

    useEffect(() => {
        fetchContent();
    }, [fetchContent]);

    useEffect(() => {
        setSelectedFilePath(selectedFilePathProp);
    }, [selectedFilePathProp]);

    return (
        <div className='tw-min-h-32'>
            <DataWrapper isLoading={contentLoading || actionLoading}>
                {!isEmpty(content?.files) &&
                    <div className='tw-flex tw-flex-wrap tw-mb-8'>
                        {map(content.files, (file) => (
                            <FilePreview
                                key={file.name}
                                className='tw-p-2'
                                name={file.name}
                                path={file.path}
                                imageSize={imageSize}
                                imageOnly={imageOnly}
                                selected={file.path === selectedFilePath}
                                onRemove={onClickRemove}
                                onSelect={onSelect}
                            />
                        ))}
                    </div>}
                {content &&
                    <DropZone
                        filesUploaded={content.files.length}
                        maxFiles={content.maxFiles}
                        options={{
                            accept: content.accept,
                            maxSize: content.maxSize,
                            multiple: false,
                            disabled: content.files.length >= content.maxFiles
                        }}
                        onUpload={onCreate}
                    />}
            </DataWrapper>
            <ConfirmModal
                title={t(T_KEYS.DELETE_MODAL.TITLE)}
                content={
                    confirmRemoveModalProps.open ?
                        `${t(T_KEYS.DELETE_MODAL.CONFIRM)} "${confirmRemoveModalProps?.data?.name}"?` : null
                }
                {...confirmRemoveModalProps}
                onClose={onCloseConfirmRemoveModal}
            />
            <InvalidFileTypeModal {...invalidFileTypeModalProps} onClose={onCloseInvalidFileTypeModal} />
        </div>
    );
};
